import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
} from '../hooks/useNotifications';

export default function NotificationBell() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: unreadCount } = useUnreadCount();
  const { data: notificationsData } = useNotifications({ limit: 10, unreadOnly: false });
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'alert':
        return '🔴';
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      default:
        return '📢';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'alert':
        return 'text-red-600';
      case 'warning':
        return 'text-orange-600';
      case 'info':
        return 'text-blue-600';
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-700';
      default:
        return 'text-gray-600';
    }
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t('notifications.timeAgo.justNow');
    if (diffMins < 60) return t('notifications.timeAgo.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('notifications.timeAgo.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('notifications.timeAgo.daysAgo', { count: diffDays });
    return date.toLocaleDateString();
  };

  const handleNotificationClick = (notification: any) => {
    if (notification.recipient_status !== 'read') {
      markAsRead.mutate(notification.id);
    }
    if (notification.action_url) {
      setIsOpen(false);
    }
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative rounded-full p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span className="text-2xl">🔔</span>
        {unreadCount !== undefined && unreadCount > 0 && (
          <span className="absolute right-0 top-0 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-96 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h3 className="text-lg font-semibold text-gray-900">{t('notifications.title')}</h3>
            {unreadCount !== undefined && unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {t('notifications.markAllAsRead')}
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {!notificationsData?.items.length ? (
              <div className="px-4 py-8 text-center text-gray-500">
                {t('notifications.noNotifications')}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notificationsData.items.map((notification) => (
                  <div
                    key={notification.id}
                    className={`px-4 py-3 transition-colors hover:bg-gray-50 ${
                      notification.recipient_status !== 'read'
                        ? 'bg-blue-50'
                        : ''
                    }`}
                  >
                    {notification.action_url ? (
                      <Link
                        to={notification.action_url}
                        onClick={() => handleNotificationClick(notification)}
                        className="block"
                      >
                        <NotificationContent notification={notification} getTypeIcon={getTypeIcon} getTypeColor={getTypeColor} getTimeAgo={getTimeAgo} />
                      </Link>
                    ) : (
                      <div onClick={() => handleNotificationClick(notification)}>
                        <NotificationContent notification={notification} getTypeIcon={getTypeIcon} getTypeColor={getTypeColor} getTimeAgo={getTimeAgo} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notificationsData && notificationsData.items.length > 0 && (
            <div className="border-t border-gray-200 px-4 py-2 text-center">
              <Link
                to="/notifications"
                onClick={() => setIsOpen(false)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {t('notifications.viewAll')}
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationContent({
  notification,
  getTypeIcon,
  getTypeColor,
  getTimeAgo,
}: {
  notification: any;
  getTypeIcon: (type: string) => string;
  getTypeColor: (type: string) => string;
  getTimeAgo: (dateString: string) => string;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex items-start space-x-3">
      <span className="text-2xl flex-shrink-0">{getTypeIcon(notification.type)}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">
          {notification.title}
        </p>
        <p className="mt-1 text-sm text-gray-600 line-clamp-2">
          {notification.message}
        </p>
        <div className="mt-1 flex items-center space-x-2">
          <span className="text-xs text-gray-500">
            {getTimeAgo(notification.created_at)}
          </span>
          {notification.entity_code && (
            <>
              <span className="text-xs text-gray-400">•</span>
              <span className="text-xs text-gray-500">
                {notification.entity_code}
              </span>
            </>
          )}
          {notification.priority === 'urgent' && (
            <>
              <span className="text-xs text-gray-400">•</span>
              <span className="text-xs font-semibold text-red-600 uppercase">
                {t('notifications.urgent')}
              </span>
            </>
          )}
        </div>
      </div>
      {notification.recipient_status !== 'read' && (
        <div className="flex-shrink-0">
          <div className="h-2 w-2 rounded-full bg-blue-600"></div>
        </div>
      )}
    </div>
  );
}
